import { type ReactNode, Suspense } from 'react';
import { t } from '@/lib/i18n';
import { type EntityType, useEntityTabs } from '@/modules/entityTabs';
import { ErrorState, LoadingState, TabPanel, Tabs } from '@/ui';
import s from './Templates.module.css';

/**
 * Entity template (IA §5.3): header · tabs · tab content. The tabs come from the registry,
 * so any module can add one (registerEntityTab) without touching the owning page.
 */
export function EntityTemplate({
  entity,
  id,
  header,
  tab,
  onTabChange,
  aside,
}: {
  entity: EntityType;
  id: string;
  header: ReactNode;
  /** The active tab key from the URL (`?tab=`); defaults to the first tab. */
  tab: string | undefined;
  onTabChange: (tab: string) => void;
  /** Optional side column (e.g. a live map) on wide containers. */
  aside?: ReactNode;
}) {
  const tabs = useEntityTabs(entity);
  const active = tabs.find((d) => d.key === tab) ?? tabs[0];
  return (
    <div className={s.entity}>
      {header}
      {tabs.length === 0 ? (
        <ErrorState title={t('Nothing to show here')} />
      ) : (
        <div className={s.entityBody}>
          <div className={s.entityMain}>
            <Tabs value={active?.key ?? ''} onChange={onTabChange} items={tabs.map((d) => ({ key: d.key, label: t(d.label) }))} label={t('Sections')}>
              {active && (
                <TabPanel value={active.key}>
                  <Suspense fallback={<LoadingState />}>
                    <active.component id={id} />
                  </Suspense>
                </TabPanel>
              )}
            </Tabs>
          </div>
          {aside && <aside className={s.entityAside}>{aside}</aside>}
        </div>
      )}
    </div>
  );
}
